import { useId, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ReviewMark, ReviewUnit } from "../../../api/content/curationApi";
import type { ReviewRequest } from "../../../api/content/reviewCollaborationApi";
import type { Discussion } from "../../../api/content/discussionsApi";
import type { Review } from "../../../api/content/reviewDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import DiscussionPanel from "../../discussion/DiscussionPanel";
import { useMemberNames } from "../../threads/data/useMemberNames";
import ReviewRequests from "./ReviewRequests";
import { unitSubject } from "./unitSubject";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import { itemKindText } from "../../workflows/inputCards/typeWords";

/**
 * The context of the unit in focus (curation-ui.md CU-10): who decides it and who
 * approved it on this head, the others' marks, its items (a curator may edit an RDF item's
 * Turtle here, on someone else's change set too), who was asked to look at it, and its
 * discussion.
 */
export default function ContextPane({
  unit,
  review,
  marks,
  discussion,
  canEdit,
  canMark,
  requests,
  canAsk,
  onEdit,
  onDiscussionChanged,
  onRequestsChanged,
  discussionRef,
}: {
  unit: ReviewUnit;
  review: Review;
  marks: readonly ReviewMark[];
  discussion: Discussion | null;
  canEdit: boolean;
  /** Whether the caller may mark this unit, as the review page decides; an empty Marks section hides otherwise (P-B, W-09f). */
  canMark: boolean;
  requests: readonly ReviewRequest[];
  canAsk: boolean;
  onEdit: (itemId: string) => void;
  onDiscussionChanged: () => void;
  onRequestsChanged: () => void;
  /** This review's discussion, so Comment (`c`) moves to it: a review beside this one has its own (ADR-0039). */
  discussionRef: Ref<HTMLElement>;
}) {
  const { t } = useTranslation();
  const decidesHeadingId = useId();
  const marksHeadingId = useId();
  const discussionHeadingId = useId();
  const people = useMemberNames(review.changeSet.projectId);
  const name = (userId: string) => (userId === people.currentUserId ? t("reviews.you") : people.nameOf(userId));
  const covered = new Set(review.summary.callerCurates.map((item) => item.itemId));
  const unitItems = unit.kind === "item" ? [{ itemId: unit.key.slice(5) }] : unit.items;
  const items = unitItems.flatMap((entry) => review.items.filter((item) => item.itemId === entry.itemId));
  const others = marks.filter((mark) => mark.unitKey === unit.key);
  return (
    <aside className="space-y-4" aria-label={t("reviews.curation.context.label")}>
      <section className="space-y-1" aria-labelledby={decidesHeadingId}>
        <h4 id={decidesHeadingId} className="app-subsection-title text-sm">{t("reviews.curation.context.decides")}</h4>
        {items.length === 0 ? <EmptyState>{t("reviews.curation.context.anyone")}</EmptyState> : null}
        <ul className="space-y-2 text-sm">
          {items.map((item) => {
            const approvedBy = review.summary.coverage.find((coverage) => coverage.item.itemId === item.itemId)?.approvedBy ?? [];
            return (
              <li key={item.itemId} className="space-y-0.5">
                <p>
                  <Chip className="mr-1">{itemKindText(item.kind, t)}</Chip>
                  <span className="font-medium">{item.name}</span>
                </p>
                <p className="app-text-muted text-xs">
                  {covered.has(item.itemId) ? t("reviews.curation.context.youCurate") : t("reviews.curation.context.othersCurate")}
                  {" · "}
                  {approvedBy.length > 0 ? t("reviews.item.approvedBy", { names: approvedBy.map(name).join(", ") }) : t("reviews.item.notApproved")}
                </p>
                <p className="flex flex-wrap gap-2 text-xs">
                  {item.newReference !== null ? (
                    <Link to={appPaths.item(item.newReference)} className="app-link underline">
                      {t("reviews.item.openNew")}
                    </Link>
                  ) : null}
                  {canEdit && item.newReference !== null && (item.kind === "ontology_module" || item.kind === "shapes") ? (
                    <AppButton
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        onEdit(item.itemId);
                      }}
                    >
                      {t("reviews.curation.edit.open")}
                    </AppButton>
                  ) : null}
                </p>
              </li>
            );
          })}
        </ul>
      </section>
      {others.length === 0 && !canMark ? null : (
        <section className="space-y-1" aria-labelledby={marksHeadingId}>
          <h4 id={marksHeadingId} className="app-subsection-title text-sm">{t("reviews.curation.context.marks")}</h4>
          {others.length === 0 ? <EmptyState>{t("reviews.curation.context.noMarks")}</EmptyState> : null}
          <ul className="space-y-0.5 text-sm">
            {others.map((mark) => (
              <li key={mark.reviewer} className={mark.current ? "" : "app-text-muted line-through"}>
                {name(mark.reviewer)}: {t(`reviews.curation.marks.short.${mark.verdict}`)}
                {mark.current ? null : <span className="ml-1 text-xs no-underline">({t("reviews.curation.context.outdated")})</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      <ReviewRequests
        changeSetId={review.changeSet.changeSetId}
        projectId={review.changeSet.projectId}
        unit={unit}
        requests={requests}
        people={people}
        itemName={(itemId) => review.items.find((item) => item.itemId === itemId)?.name ?? itemId}
        canAsk={canAsk}
        onChanged={onRequestsChanged}
      />
      <DiscussionPanel
        projectId={review.changeSet.projectId}
        subject={unitSubject(unit, review.changeSet.changeSetId)}
        changeSetId={review.changeSet.changeSetId}
        preloaded={discussion}
        onChanged={onDiscussionChanged}
        frame={(panel) => (
          <section ref={discussionRef} className="space-y-1" aria-labelledby={discussionHeadingId} tabIndex={-1}>
            <h4 id={discussionHeadingId} className="app-subsection-title text-sm">{t("reviews.curation.context.discussion")}</h4>
            {panel}
          </section>
        )}
      />
    </aside>
  );
}
